/**
 * In-browser TSX compiler using esbuild-wasm.
 *
 * Compiles AI-generated Remotion scene code into live React components
 * that can be rendered by the Remotion Player in the browser.
 */

import {useCallback, useEffect, useRef, useState} from 'react';

// ---------------------------------------------------------------------------
// esbuild-wasm initialization
// ---------------------------------------------------------------------------

let esbuildReady: Promise<typeof import('esbuild-wasm')> | null = null;

async function getEsbuild(): Promise<typeof import('esbuild-wasm')> {
  if (esbuildReady) return esbuildReady;

  esbuildReady = (async () => {
    const esbuild = await import('esbuild-wasm');
    await esbuild.initialize({
      wasmURL: 'https://unpkg.com/esbuild-wasm@0.24.2/esbuild.wasm',
    });
    return esbuild;
  })();

  return esbuildReady;
}

// ---------------------------------------------------------------------------
// Compilation
// ---------------------------------------------------------------------------

export interface CompileResult {
  code: string;
  error: string | null;
}

/**
 * Compile a single TSX source string into executable JavaScript.
 * Returns { code, error } -- on failure, code is empty and error is set.
 */
export async function compileTsx(source: string): Promise<CompileResult> {
  try {
    const esbuild = await getEsbuild();

    const result = await esbuild.transform(source, {
      loader: 'tsx',
      jsx: 'automatic',
      jsxImportSource: 'react',
      target: 'es2020',
      format: 'esm',
    });

    return {code: result.code, error: null};
  } catch (err) {
    return {
      code: '',
      error: err instanceof Error ? err.message : String(err),
    };
  }
}

/**
 * Compile TSX source and evaluate it into a live React component.
 *
 * The scene code must export a default React component. The compiled
 * module is evaluated with the necessary runtime imports (react, remotion)
 * injected via a module map.
 */
export async function compileToComponent(
  source: string,
): Promise<{component: React.FC | null; error: string | null}> {
  const {code, error} = await compileTsx(source);
  if (error) return {component: null, error};

  try {
    // Create a blob URL with the compiled JS and import it as a module.
    // We rewrite bare imports to point to CDN versions so the browser
    // can resolve them.
    const rewritten = rewriteImports(code);
    const blob = new Blob([rewritten], {type: 'application/javascript'});
    const url = URL.createObjectURL(blob);

    try {
      const mod = await import(/* @vite-ignore */ url);
      const component = mod.default ?? mod.Scene ?? mod.Main ?? null;

      if (!component) {
        return {
          component: null,
          error: 'Scene must export a default component',
        };
      }

      return {component, error: null};
    } finally {
      URL.revokeObjectURL(url);
    }
  } catch (err) {
    return {
      component: null,
      error: err instanceof Error ? err.message : String(err),
    };
  }
}

/**
 * Rewrite bare module imports to use import maps / globals.
 *
 * esbuild transforms JSX to `import { jsx } from "react/jsx-runtime"` etc.
 * In the browser, these need to resolve to the already-loaded React modules.
 * We rewrite them to use the global `window.__CASCADE_MODULES__` map.
 */
function rewriteImports(code: string): string {
  // Replace import statements with destructuring from the global module map.
  // This handles: import { x } from "react", import { y } from "remotion", etc.
  return code
    .replace(
      /import\s*\{([^}]+)\}\s*from\s*["']react\/jsx-runtime["']/g,
      'const {$1} = window.__CASCADE_MODULES__["react/jsx-runtime"]',
    )
    .replace(
      /import\s*\{([^}]+)\}\s*from\s*["']react["']/g,
      'const {$1} = window.__CASCADE_MODULES__["react"]',
    )
    .replace(
      /import\s*(\w+)\s*from\s*["']react["']/g,
      'const $1 = window.__CASCADE_MODULES__["react"]',
    )
    .replace(
      /import\s*\{([^}]+)\}\s*from\s*["']remotion["']/g,
      'const {$1} = window.__CASCADE_MODULES__["remotion"]',
    )
    .replace(
      /import\s*\{([^}]+)\}\s*from\s*["']@remotion\/([^"']+)["']/g,
      'const {$1} = window.__CASCADE_MODULES__["@remotion/$2"]',
    );
}

// ---------------------------------------------------------------------------
// React hook
// ---------------------------------------------------------------------------

interface UseCompilerOptions {
  /** Debounce delay in ms before recompiling after source changes. */
  debounceMs?: number;
}

interface UseCompilerResult {
  component: React.FC | null;
  error: string | null;
  compiling: boolean;
  /** Manually trigger recompilation. */
  recompile: () => void;
}

/**
 * React hook that compiles TSX source into a live React component.
 * Recompiles automatically when source changes (debounced).
 */
export function useCompiler(
  source: string | null,
  options: UseCompilerOptions = {},
): UseCompilerResult {
  const {debounceMs = 300} = options;
  const [component, setComponent] = useState<React.FC | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [compiling, setCompiling] = useState(false);
  const versionRef = useRef(0);

  const compile = useCallback(async () => {
    if (!source) {
      setComponent(null);
      setError(null);
      return;
    }

    const version = ++versionRef.current;
    setCompiling(true);

    const result = await compileToComponent(source);

    // Only apply if this is still the latest compilation
    if (version === versionRef.current) {
      setComponent(() => result.component);
      setError(result.error);
      setCompiling(false);
    }
  }, [source]);

  useEffect(() => {
    if (!source) {
      setComponent(null);
      setError(null);
      return;
    }

    const timer = setTimeout(compile, debounceMs);
    return () => clearTimeout(timer);
  }, [source, debounceMs, compile]);

  return {component, error, compiling, recompile: compile};
}
